import React, { useState, useEffect, useRef } from "react";
import { 
    Card, Row, Col, Button, Badge, Table, Spinner, Alert, 
    Modal, Form, Tooltip, OverlayTrigger 
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";
import './Factures.css';

export default function DetailBonLivraison() {
    const navigate = useNavigate();
    const { id } = useParams();
    const printRef = useRef();
    
    const [bl, setBl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    
    // États pour les modals
    const [showLivraisonModal, setShowLivraisonModal] = useState(false);
    const [showAnnulationModal, setShowAnnulationModal] = useState(false);
    const [showPreparationModal, setShowPreparationModal] = useState(false);
    
    // États pour les alertes
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    
    // État pour les informations de livraison
    const [livraisonInfo, setLivraisonInfo] = useState({
        date_livraison_reelle: new Date().toISOString().split('T')[0],
        commentaire: '',
        nom_livreur: '',
        signature: false
    });

    useEffect(() => {
        fetchBonLivraison();
        fetchSocieteInfos();
        loadLogoBase64();
    }, [id]);

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setSocieteInfos(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                
                img.onerror = () => {
                    console.warn("Impossible de charger le logo");
                    setLogoBase64('');
                    resolve('');
                };
                
                img.src = logo;
            } catch (error) {
                console.error("Erreur chargement logo:", error);
                setLogoBase64('');
                resolve('');
            }
        });
    };

    const fetchBonLivraison = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setBl(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handleMarquerLivre = async () => {
        setSaving(true);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'marquer_livre',
                        id: id,
                        date_livraison_reelle: livraisonInfo.date_livraison_reelle,
                        nom_livreur: livraisonInfo.nom_livreur,
                        commentaire: livraisonInfo.commentaire
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowLivraisonModal(false);
                fetchBonLivraison();
                showSuccess("✅ Bon de livraison marqué comme livré");
            } else {
                showError(result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleAnnuler = async () => {
        setSaving(true);
        
        try {
            const token = localStorage.getItem("token");
            
            const getResponse = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!getResponse.ok) throw new Error("Erreur chargement");
            
            const getResult = await getResponse.json();
            const blData = getResult.data;
            
            const updateResponse = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...blData,
                        statut: 'annule'
                    })
                }
            );

            const updateResult = await updateResponse.json();

            if (updateResult.success) {
                setShowAnnulationModal(false);
                fetchBonLivraison();
                showSuccess("❌ Bon de livraison annulé");
            } else {
                showError(updateResult.message || "Erreur lors de l'annulation");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handlePreparer = async () => {
        setSaving(true);
        
        try {
            const token = localStorage.getItem("token");
            
            const getResponse = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!getResponse.ok) throw new Error("Erreur chargement");
            
            const getResult = await getResponse.json();
            const blData = getResult.data;
            
            const updateResponse = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...blData,
                        statut: 'prepare'
                    })
                }
            );

            const updateResult = await updateResponse.json();

            if (updateResult.success) {
                setShowPreparationModal(false);
                fetchBonLivraison();
                showSuccess("📦 Bon de livraison marqué comme préparé");
            } else {
                showError(updateResult.message || "Erreur lors de la préparation");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com',
        rc: societeInfos?.rc || 'RC123456',
        nif: societeInfos?.nif || 'NIF123456',
        patente: societeInfos?.patente || 'PAT123456'
    };

    const genererHTMLImpression = () => {
        if (!bl) return '';

        const statutConfig = {
            brouillon: { class: 'draft', label: 'BROUILLON' },
            prepare: { class: 'prepared', label: 'PRÉPARÉ' },
            livre: { class: 'delivered', label: 'LIVRÉ' },
            annule: { class: 'cancelled', label: 'ANNULÉ' }
        };

        const statut = statutConfig[bl.statut] || statutConfig.draft;
        const totalProduits = bl?.lignes?.reduce((acc, l) => acc + (parseInt(l.quantite) || 0), 0) || 0;
        const logoSrc = logoBase64;
        const logoText = company.name.substring(0, 2).toUpperCase();

        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Bon de livraison ${bl.numero_bl || ''}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { 
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        background: #eef2f7;
                        padding: 20px;
                        color: #1e293b;
                        font-size: 11px;
                    }
                    .document-container {
                        margin: 0 auto;
                        background: white;
                        border-radius: 12px;
                        overflow: hidden;
                    }
                    
                    .document-header {
                        background: white;
                        padding: 5px;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .header-top {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 15px;
                    }
                    .logo-area {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }
                    .logo-img {
                        width: auto;
                        height: 60px;
                    }
                    .logo-fallback {
                        width: 50px;
                        height: 50px;
                        background: linear-gradient(135deg, #1a3c5e, #2c5a7a);
                        border-radius: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 20px;
                        font-weight: bold;
                        color: white;
                    }
                    .company-name {
                        font-size: 16px;
                        font-weight: 700;
                        color: #1a3c5e;
                    }
                    .document-title {
                        text-align: right;
                    }
                    .document-title h2 {
                        font-size: 18px;
                        font-weight: 700;
                        color: #1a3c5e;
                        letter-spacing: 1px;
                        margin-bottom: 4px;
                    }
                    .document-number {
                        font-size: 10px;
                        color: #64748b;
                    }
                    .status-badge {
                        display: inline-block;
                        padding: 3px 10px;
                        border-radius: 20px;
                        font-size: 9px;
                        font-weight: 600;
                        margin-top: 6px;
                    }
                    .status-delivered { background: #d1fae5; color: #065f46; }
                    .status-prepared { background: #dbeafe; color: #1e40af; }
                    .status-draft { background: #f1f5f9; color: #475569; }
                    .status-cancelled { background: #fee2e2; color: #991b1b; }
                    
                    .company-details {
                        display: flex;
                        justify-content: space-between;
                        font-size: 9px;
                        color: #64748b;
                        padding: 5px;
                        border-top: 1px solid #e2e8f0;
                    }
                    
                    .document-body {
                        padding: 5px 0px;
                    }
                    
                    .client-card {
                        background: #f8fafc;
                        border-radius: 10px;
                        padding: 15px;
                        margin-bottom: 20px;
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        border: 1px solid #e2e8f0;
                    }
                    .info-label {
                        font-size: 9px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: #64748b;
                        margin-bottom: 6px;
                    }
                    .client-name {
                        font-size: 14px;
                        font-weight: 700;
                        color: #0f172a;
                        margin-bottom: 6px;
                    }
                    .client-details {
                        font-size: 10px;
                        color: #475569;
                        line-height: 1.4;
                    }
                    .date-item {
                        margin-bottom: 6px;
                    }
                    .date-label {
                        font-size: 8px;
                        color: #64748b;
                    }
                    .date-value {
                        font-size: 11px;
                        font-weight: 600;
                        color: #0f172a;
                    }
                    
                    .delivery-card {
                        background: #f8fafc;
                        border-radius: 0px;
                        padding: 15px;
                        margin-bottom: 20px;
                        border: 1px solid #e2e8f0;
                        border-left: 2px solid #1a3c5e;
                    }
                    .delivery-title {
                        font-size: 11px;
                        font-weight: 600;
                        margin-bottom: 10px;
                        color: #1e293b;
                    }
                    .delivery-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 10px;
                    }
                    .delivery-item strong {
                        display: block;
                        font-size: 8px;
                        color: #64748b;
                        margin-bottom: 3px;
                    }
                    .delivery-item span {
                        font-size: 10px;
                        color: #1e293b;
                    }
                    
                    .products-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                        font-size: 10px;
                    }
                    .products-table th {
                        background: #f8fafc;
                        padding: 8px 10px;
                        text-align: left;
                        font-weight: 600;
                        color: #1e293b;
                        border-bottom: 1.5px solid #e2e8f0;
                    }
                    .products-table td {
                        padding: 8px 10px;
                        border-bottom: 1px solid #e2e8f0;
                        color: #334155;
                    }
                    .text-center { text-align: center; }
                    
                    .totals {
                        display: flex;
                        justify-content: flex-end;
                        margin-top: 15px;
                    }
                    .totals-card {
                        width: 280px;
                        background: #f8fafc;
                        border-radius: 10px;
                        padding: 15px;
                    }
                    .total-line {
                        display: flex;
                        justify-content: space-between;
                        padding: 5px 0;
                        font-size: 10px;
                        border-bottom: 1px dashed #e2e8f0;
                    }
                    .total-line:last-child {
                        border-bottom: none;
                    }
                    .total-line.highlight {
                        background: #eef2ff;
                        margin: 8px -15px;
                        padding: 8px 15px;
                        font-weight: 600;
                    }
                    
                    .notes-box {
                        margin-top: 15px;
                        padding: 10px;
                        background: #fffbeb;
                        border-left: 2px solid #f59e0b;
                        border-radius: 0px;
                        font-size: 9px;
                    }
                    
                    .signature-section {
                        margin-top: 25px;
                        padding-top: 15px;
                        border-top: 1px solid #e2e8f0;
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-end;
                    }
                    .signature-box {
                        text-align: center;
                    }
                    .signature-line {
                        width: 150px;
                        height: 1px;
                        background: #1a3c5e;
                        margin-bottom: 6px;
                    }
                    .signature-label {
                        font-size: 8px;
                        color: #64748b;
                    }
                    
                    .document-footer {
                        background: #f8fafc;
                        padding: 12px 25px;
                        text-align: center;
                        font-size: 8px;
                        color: #64748b;
                        border-top: 1px solid #e2e8f0;
                    }
                    
                    @media print {
                        body { background: white; padding: 0; }
                        .document-container { box-shadow: none; border-radius: 0; }
                    }
                </style>
            </head>
            <body>
                <div class="document-container">
                    <div class="document-header">
                        <div class="header-top">
                            <div class="logo-area">
                                ${logoSrc ? `<img src="${logoSrc}" alt="Logo" class="logo-img" />` : `<div class="logo-fallback">${logoText}</div>`}
                            </div>
                            <div class="document-title">
                                <h2>BON DE LIVRAISON</h2>
                                <div class="document-number">N° ${bl.numero_bl || ''}</div>
                                <div class="status-badge status-${statut.class}">${statut.label}</div>
                            </div>
                        </div>
                        <div class="company-details">
                            <table style="width: 100%; border: 0px;">
                                <tr>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0; width: 50%;">
                                        <strong>Tél:</strong> ${company.phone}
                                    </td>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0; width: 50%;">
                                        <strong>Adresse:</strong> ${company.address}
                                    </td>
                                </tr>
                                <tr>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0;">
                                        <strong>Email:</strong> ${company.email}
                                    </td>
                                    <td style="padding: 6px 8px; border: 0px solid #e2e8f0;">
                                        <strong>Site web:</strong> ${company.site_web}
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                    
                    <div class="document-body">
                        <div class="client-card">
                            <div>
                                <div class="info-label">CLIENT</div>
                                <div class="client-name">${bl.client_nom || 'Client non spécifié'}</div>
                                <div class="client-details">
                                    <div>Code: ${bl.code_client || ''}</div>
                                    ${bl.client_telephone ? `<div>${bl.client_telephone}</div>` : ''}
                                    ${bl.client_email ? `<div>✉ ${bl.client_email}</div>` : ''}
                                    ${bl.client_adresse ? `<div>${bl.client_adresse}</div>` : ''}
                                </div>
                            </div>
                            <div class="dates">
                                <div class="date-item">
                                    <div class="date-label">Date de livraison</div>
                                    <div class="date-value">${formatDate(bl.date_livraison)}</div>
                                </div>
                                ${bl.numero_facture ? `
                                    <div class="date-item">
                                        <div class="date-label">Facture associée</div>
                                        <div class="date-value">${bl.numero_facture}</div>
                                    </div>
                                ` : ''}
                            </div>
                        </div>
                        
                        <div class="delivery-card">
                            <div class="delivery-title">
                                INFORMATIONS DE LIVRAISON
                            </div>
                            <div class="delivery-grid">
                                <div class="delivery-item">
                                    <strong>Destinataire</strong>
                                    <span>${bl.destinataire || bl.client_nom || 'Non spécifié'}</span>
                                </div>
                                <div class="delivery-item">
                                    <strong>Adresse de livraison</strong>
                                    <span>${bl.adresse_livraison || bl.client_adresse || 'Non spécifiée'}</span>
                                </div>
                            </div>
                        </div>
                        
                        ${bl.notes ? `
                            <div class="notes-box">
                                <strong>Note :</strong> ${bl.notes}
                            </div>
                        ` : ''}
                        
                        <table class="products-table">
                            <thead>
                                <tr>
                                    <th>Réf.</th>
                                    <th>Désignation</th>
                                    <th class="text-center">Qté</th>
                                    <th>Lot</th>
                                    <th>Péremption</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${bl?.lignes && bl.lignes.length > 0 
                                    ? bl.lignes.map(ligne => `
                                        <tr>
                                            <td><span style="font-family: monospace;">${ligne.reference || ''}</span></td>
                                            <td>${ligne.designation || ''}</td>
                                            <td class="text-center fw-bold">${ligne.quantite || 0}</td>
                                            <td>${ligne.lot || '-'}</td>
                                            <td>${ligne.date_peremption ? formatDate(ligne.date_peremption) : '-'}</td>
                                        </tr>
                                    `).join('')
                                    : '<tr><td colspan="5" style="text-align: center; padding: 20px;">Aucun produit dans ce bon de livraison</span></td></tr>'
                                }
                            </tbody>
                        </table>
                        
                        <div class="totals">
                            <div class="totals-card">
                                <div class="total-line">
                                    <span>Nombre de références</span>
                                    <span><strong>${bl?.lignes?.length || 0}</strong></span>
                                </div>
                                <div class="total-line highlight">
                                    <span>Quantité totale</span>
                                    <span><strong style="color: #1a3c5e;">${totalProduits}</strong></span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="signature-section">
                            <div class="signature-box">
                                <div class="signature-line"></div>
                                <div class="signature-label">Cachet et signature du transporteur</div>
                            </div>
                            <div class="signature-box">
                                <div class="signature-line"></div>
                                <div class="signature-label">Cachet et signature du client</div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="document-footer">
                        <div>Merci de votre confiance</div>
                        <div style="margin-top: 4px;">${company.name} • ${company.phone} • ${company.email}</div>
                        <div style="margin-top: 4px;">RC: ${company.rc} | NIF: ${company.nif} | Patente: ${company.patente}</div>
                    </div>
                </div>
            </body>
            </html>
        `;
    };

    const handlePrint = () => {
        const htmlContent = genererHTMLImpression();
        
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            }, 1000);
        };
        
        showSuccess("Document envoyé à l'impression");
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: '#64748b', icon: 'ri-draft-line', label: 'Brouillon' },
            prepare: { color: '#3b82f6', icon: 'ri-stack-line', label: 'Préparé' },
            livre: { color: '#10b981', icon: 'ri-check-double-line', label: 'Livré' },
            annule: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Annulé' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <span className="status-badge" style={{ 
                background: `${config.color}15`, 
                color: config.color,
                padding: '0.5rem 1rem',
                borderRadius: '30px',
                fontSize: '0.75rem',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
            }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du bon de livraison...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !bl) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Bon de livraison non trouvé"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate('/ventes/blListe')}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour aux BL
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const totalProduits = bl.lignes?.reduce((acc, l) => acc + l.quantite, 0) || 0;

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }} ref={printRef}>
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-truck-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Détail du bon de livraison</h4>
                                    <p className="text-muted small mb-0">Consultez et gérez les informations du bon de livraison</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <ActionButton
                                variant="outline-secondary"
                                icon="ri-arrow-left-line"
                                onClick={() => navigate(-1)}
                                tooltip="Retour à la liste"
                            />
                            <ActionButton
                                variant="outline-primary"
                                icon="ri-printer-line"
                                onClick={handlePrint}
                                tooltip="Imprimer"
                            />
                            {bl.statut === 'brouillon' && (
                                <>
                                    <ActionButton
                                        variant="outline-primary"
                                        icon="ri-pencil-line"
                                        onClick={() => navigate(`/ventes/bl/modifier/${bl.id_bon_livraison}`)}
                                        tooltip="Modifier"
                                    />
                                    <ActionButton
                                        variant="outline-success"
                                        icon="ri-stack-line"
                                        onClick={() => setShowPreparationModal(true)}
                                        tooltip="Préparer"
                                    />
                                </>
                            )}
                            {bl.statut === 'prepare' && (
                                <ActionButton
                                    variant="outline-success"
                                    icon="ri-check-double-line"
                                    onClick={() => setShowLivraisonModal(true)}
                                    tooltip="Marquer comme livré"
                                />
                            )}
                            {bl.statut !== 'livre' && bl.statut !== 'annule' && (
                                <ActionButton
                                    variant="outline-danger"
                                    icon="ri-close-circle-line"
                                    onClick={() => setShowAnnulationModal(true)}
                                    tooltip="Annuler"
                                />
                            )}
                        </div>
                    </div>

                    {/* Carte d'identité */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center">
                                <Col md={4}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-barcode-line text-primary fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">N° BL</small>
                                            <h4 className="mb-0">{bl.numero_bl}</h4>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-calendar-line text-success fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Date livraison</small>
                                            <h5 className="mb-0">{formatDate(bl.date_livraison)}</h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-stack-line text-info fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Articles</small>
                                            <h5 className="mb-0">{bl.lignes?.length || 0} produits</h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="text-end">{getStatutBadge(bl.statut)}</div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Informations client */}
                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-user-line me-2"></i>Informations client</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3">{bl.client_nom}</h5>
                                    <p className="mb-1"><i className="ri-barcode-line me-2"></i>Code: {bl.code_client}</p>
                                    {bl.client_telephone && <p className="mb-1"><i className="ri-phone-line me-2"></i>{bl.client_telephone}</p>}
                                    {bl.client_email && <p className="mb-1"><i className="ri-mail-line me-2"></i>{bl.client_email}</p>}
                                    <p className="mb-0"><i className="ri-map-pin-line me-2"></i>{bl.client_adresse || 'Adresse non spécifiée'}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-truck-line me-2"></i>Informations de livraison</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <div className="bg-light p-3 rounded-3">
                                                <small className="text-muted d-block">Destinataire</small>
                                                <strong>{bl.destinataire || bl.client_nom}</strong>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="bg-light p-3 rounded-3">
                                                <small className="text-muted d-block">Facture associée</small>
                                                <strong>{bl.numero_facture || 'Aucune'}</strong>
                                            </div>
                                        </Col>
                                        <Col md={12}>
                                            <div className="bg-light p-3 rounded-3">
                                                <small className="text-muted d-block">Adresse de livraison</small>
                                                <strong>{bl.adresse_livraison || bl.client_adresse || 'Non spécifiée'}</strong>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Informations livreur */}
                    {(bl.nom_livreur || bl.commentaire) && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Header className="bg-transparent border-0 pt-4">
                                <h6 className="mb-0"><i className="ri-user-star-line me-2"></i>Informations livreur</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    {bl.nom_livreur && (
                                        <Col md={6}>
                                            <div className="bg-light p-3 rounded-3">
                                                <small className="text-muted d-block">Nom du livreur</small>
                                                <strong>{bl.nom_livreur}</strong>
                                            </div>
                                        </Col>
                                    )}
                                    {bl.commentaire && (
                                        <Col md={6}>
                                            <div className="bg-light p-3 rounded-3">
                                                <small className="text-muted d-block">Commentaire</small>
                                                <strong>{bl.commentaire}</strong>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Notes */}
                    {bl.notes && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Header className="bg-transparent border-0 pt-4">
                                <h6 className="mb-0"><i className="ri-file-text-line me-2"></i>Notes</h6>
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-0">{bl.notes}</p>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Lignes du BL */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-shopping-cart-line text-primary"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Produits à livrer</h6>
                                </div>
                                <Badge bg="light" className="px-3 py-2 rounded-pill">
                                    <i className="ri-stack-line me-1"></i>
                                    Total: {totalProduits} unités
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="mb-0 align-middle products-table">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <th style={{ width: '40%' }}>Désignation</th>
                                            <th className="text-center">Qté</th>
                                            <th>Lot</th>
                                            <th className="pe-4">Date péremption</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bl.lignes?.map((ligne, index) => (
                                            <tr key={index} className="product-row">
                                                <td className="ps-4">
                                                    <span className="reference-badge">
                                                        <i className="ri-barcode-line me-1 fs-10"></i>
                                                        {ligne.reference}
                                                    </span>
                                                </td>
                                                <td className="fw-semibold">{ligne.designation}</td>
                                                <td className="text-center">
                                                    <span className="quantity-badge">{ligne.quantite}</span>
                                                </td>
                                                <td>{ligne.lot || '-'}</td>
                                                <td className="pe-4">{ligne.date_peremption ? formatDate(ligne.date_peremption) : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* MODAL DE CONFIRMATION DE LIVRAISON */}
            <Modal show={showLivraisonModal} onHide={() => setShowLivraisonModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-check-double-line text-success" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold">Confirmer la livraison</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <p className="text-center text-muted mb-4">
                        Êtes-vous sûr de vouloir marquer ce bon de livraison comme <strong className="text-success">livré</strong> ?
                    </p>

                    <Form.Group className="mb-3">
                        <Form.Label>Date de livraison réelle</Form.Label>
                        <Form.Control
                            type="date"
                            value={livraisonInfo.date_livraison_reelle}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, date_livraison_reelle: e.target.value})}
                            className="rounded-pill"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Nom du livreur</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Nom du transporteur / livreur"
                            value={livraisonInfo.nom_livreur}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, nom_livreur: e.target.value})}
                            className="rounded-pill"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Commentaire (optionnel)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={livraisonInfo.commentaire}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, commentaire: e.target.value})}
                            placeholder="Observations sur la livraison..."
                            className="rounded-3"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="signature"
                            label="Je confirme que la livraison a été effectuée"
                            checked={livraisonInfo.signature}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, signature: e.target.checked})}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowLivraisonModal(false)} className="px-4 rounded-pill">
                        Annuler
                    </Button>
                    <Button variant="success" onClick={handleMarquerLivre} disabled={saving || !livraisonInfo.signature} className="px-5 rounded-pill">
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Confirmation...
                            </>
                        ) : (
                            <>
                                <i className="ri-check-double-line me-2"></i>
                                Confirmer
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL DE CONFIRMATION DE PRÉPARATION */}
            <Modal show={showPreparationModal} onHide={() => setShowPreparationModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-stack-line text-primary" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold">Préparer le bon</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2 text-center">
                    <p className="text-muted mb-4">
                        Confirmez-vous la préparation de ce bon de livraison ?
                    </p>
                    <Alert variant="info" className="text-start rounded-3">
                        <i className="ri-information-line me-2"></i>
                        Une fois préparé, les produits seront déduits du stock.
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowPreparationModal(false)} className="px-4 rounded-pill">
                        Annuler
                    </Button>
                    <Button variant="primary" onClick={handlePreparer} disabled={saving} className="px-5 rounded-pill" style={{ background: '#21ACCD', border: 'none' }}>
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Préparation...
                            </>
                        ) : (
                            <>
                                <i className="ri-stack-line me-2"></i>
                                Confirmer
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL DE CONFIRMATION D'ANNULATION */}
            <Modal show={showAnnulationModal} onHide={() => setShowAnnulationModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-close-circle-line text-danger" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-danger">Annuler le bon</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2 text-center">
                    <p className="text-muted mb-4">
                        Êtes-vous sûr de vouloir annuler ce bon de livraison ?
                    </p>
                    <Alert variant="danger" className="rounded-3">
                        <i className="ri-alert-line me-2"></i>
                        Cette action est irréversible.
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowAnnulationModal(false)} className="px-4 rounded-pill">
                        Retour
                    </Button>
                    <Button variant="danger" onClick={handleAnnuler} disabled={saving} className="px-5 rounded-pill">
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Annulation...
                            </>
                        ) : (
                            <>
                                <i className="ri-close-circle-line me-2"></i>
                                Confirmer
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .action-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                }
                .btn-outline-secondary:hover {
                    background: linear-gradient(135deg, #64748b, #475569);
                    border-color: #64748b;
                    color: white;
                }
                .btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }
                .btn-outline-danger:hover {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    border-color: #ef4444;
                    color: white;
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.4rem 1rem;
                    border-radius: 30px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                }
                
                .quantity-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 50px;
                    padding: 0.25rem 0.5rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.85rem;
                    color: #1e293b;
                }
                
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .products-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .products-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .product-row {
                    transition: all 0.2s ease;
                }
                
                .product-row:hover {
                    background: #f8fafc;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
            `}</style>
        </>
    );
}